
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Target, User2, Dumbbell, TrendingUp, ActivitySquare } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from "recharts";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguagePath } from "@/utils/language";
import { useLanguage } from "@/components/LanguageContext";

export const StudentProgress = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslations("progress");
  const { getPath } = useLanguagePath();
  const { language } = useLanguage();
  
  const dateLocale = language === 'pt' ? ptBR : undefined;

  const { data: student } = useQuery({
    queryKey: ["student", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("students")
        .select(`
          *,
          physical_assessments (
            *
          ),
          training_sessions (
            created_at,
            status,
            created_by_type,
            workout_id,
            training_session_exercises (
              id,
              weight,
              sets_completed,
              is_cardio,
              training_session_exercise_sets (
                weight,
                time,
                reps
              )
            )
          )
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const assessmentData = student?.physical_assessments
    ?.sort((a: any, b: any) => new Date(a.assessment_date).getTime() - new Date(b.assessment_date).getTime())
    ?.map((assessment: any) => ({
      date: format(new Date(assessment.assessment_date), "dd/MM/yyyy", { locale: dateLocale }),
      peso: assessment.weight,
      gordura: assessment.body_fat_percentage,
      muscular: assessment.muscle_mass,
    }));

  // Count all completed sessions, regardless of who created them
  const completedSessions = student?.training_sessions?.filter(
    (session: any) => session.status === "completed"
  ).length;

  const lastAssessment = student?.physical_assessments
    ?.sort((a: any, b: any) => new Date(b.assessment_date).getTime() - new Date(a.assessment_date).getTime())[0];

  const trainingSessionsData = student?.training_sessions
    ?.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    ?.map((session: any) => {
      // Calculate total and max weight from the exercises
      let totalWeight = 0;
      let maxWeight = 0;
      let totalSets = 0;

      session.training_session_exercises?.forEach((exercise: any) => {
        // Skip cardio exercises for weight calculations
        if (exercise.is_cardio) return;
        
        // Count total sets completed
        totalSets += exercise.sets_completed || 0;
        
        // Process each set to calculate total and max weight
        exercise.training_session_exercise_sets?.forEach((set: any) => {
          const weight = set.weight ? parseFloat(set.weight) : 0;
          const reps = set.reps ? parseInt(set.reps, 10) : 0;
          
          if (weight > 0) {
            // Calculate volume (weight x reps) for this set
            const setVolume = weight * reps;
            totalWeight += setVolume;
            
            // Track maximum weight
            if (weight > maxWeight) {
              maxWeight = weight;
            }
          }
        });
      });

      return {
        date: format(new Date(session.created_at), "dd/MM/yyyy", { locale: dateLocale }),
        pesoMáximo: maxWeight,
        pesoTotal: totalWeight,
        séries: totalSets,
        createdBy: session.created_by_type // Add information about who created the session
      };
    });

  const getSafeProgress = (data: any[], key: string) => {
    if (!data || data.length < 2) return "0";
    const nonZeroData = data.filter(d => typeof d[key] === 'number' && d[key]);
    if (nonZeroData.length < 2) return "0";
    const first = nonZeroData[0][key];
    const last = nonZeroData[nonZeroData.length - 1][key];
    if (!first || !last || first === 0) return "0";
    return ((last - first) / first * 100).toFixed(1);
  };

  const weightProgress = getSafeProgress(assessmentData || [], "peso");
  const trainingSessionsNonEmpty = trainingSessionsData && trainingSessionsData.length > 0;
  const strengthProgress = trainingSessionsNonEmpty
    ? getSafeProgress(trainingSessionsData, "pesoMáximo")
    : t("studentProgress.charts.noData");

  const customTooltip = (props: any) => {
    const { active, payload, label } = props;

    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border p-2 rounded-lg shadow-lg">
          <p className="text-sm text-muted-foreground">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm">
              {entry.name}: {entry.value}
              {entry.name.includes("peso") || entry.name.includes("Weight") ? " kg" : ""}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <h2 className="text-3xl font-bold tracking-tight gradient-text dark:text-white">
            {t("studentProgress.title")}
          </h2>
          <p className="text-muted-foreground dark:text-gray-300">
            {t("studentProgress.subtitle")}
          </p>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href={getPath("/dashboard")} className="text-flame hover:text-flame/80 dark:text-flame dark:hover:text-flame/80">{t("breadcrumbs.dashboard")}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={getPath("/dashboard/progress")} className="text-flame hover:text-flame/80 dark:text-flame dark:hover:text-flame/80">{t("breadcrumbs.progress")}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-medium dark:text-white">
                  {student?.first_name} {student?.last_name}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <Button variant="outline" onClick={() => navigate(getPath("/dashboard/progress"))} className="flex items-center gap-2 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 dark:border-gray-600">
          <ArrowLeft className="h-4 w-4" />
          {t("studentProgress.back")}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card className="dark:border-gray-700 dark:bg-gray-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium dark:text-white">{t("studentProgress.stats.completedSessions")}</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground dark:text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold dark:text-white">{completedSessions || 0}</div>
          </CardContent>
        </Card>

        <Card className="dark:border-gray-700 dark:bg-gray-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium dark:text-white">{t("studentProgress.stats.weightProgress")}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground dark:text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold dark:text-white">{weightProgress}%</div>
          </CardContent>
        </Card>

        <Card className="dark:border-gray-700 dark:bg-gray-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium dark:text-white">{t("studentProgress.stats.strengthProgress")}</CardTitle>
            <Dumbbell className="h-4 w-4 text-muted-foreground dark:text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold dark:text-white">
              {strengthProgress}
              {strengthProgress !== t("studentProgress.charts.noData") && "%"}
            </div>
          </CardContent>
        </Card>

        <Card className="dark:border-gray-700 dark:bg-gray-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium dark:text-white">{t("studentProgress.stats.goal")}</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground dark:text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold dark:text-white">{student?.goals || t("studentProgress.stats.notDefined")}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="dark:border-gray-700 dark:bg-gray-800">
          <CardHeader>
            <CardTitle className="dark:text-white">{t("studentProgress.charts.weightEvolution")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={assessmentData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#474747" />
                  <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <YAxis stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <Tooltip 
                    content={customTooltip}
                    contentStyle={{ 
                      backgroundColor: 'rgba(31, 41, 55, 0.9)', 
                      border: '1px solid #374151',
                      color: '#E5E7EB'
                    }} 
                  />
                  <Line
                    type="monotone"
                    dataKey="peso"
                    stroke="#8884d8"
                    name={t("studentProgress.charts.weight")}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="dark:border-gray-700 dark:bg-gray-800">
          <CardHeader>
            <CardTitle className="dark:text-white">{t("studentProgress.charts.bodyComposition")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={assessmentData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#474747" />
                  <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <YAxis stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <Tooltip 
                    content={customTooltip}
                    contentStyle={{ 
                      backgroundColor: 'rgba(31, 41, 55, 0.9)', 
                      border: '1px solid #374151',
                      color: '#E5E7EB'
                    }} 
                  />
                  <Line
                    type="monotone"
                    dataKey="gordura"
                    stroke="#82ca9d"
                    name={t("studentProgress.charts.fatPercentage")}
                  />
                  <Line
                    type="monotone"
                    dataKey="muscular"
                    stroke="#ffc658"
                    name={t("studentProgress.charts.muscleMass")}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="dark:border-gray-700 dark:bg-gray-800">
          <CardHeader className="flex flex-row justify-between items-center">
            <CardTitle className="dark:text-white">{t("studentProgress.charts.strengthProgress")}</CardTitle>
            <div className="flex items-center text-xs space-x-4">
              <div className="flex items-center">
                <span className="w-3 h-3 inline-block bg-[#ff7300] mr-1 rounded-full"></span>
                <span className="dark:text-gray-300">{t("studentProgress.charts.trainerRecorded")}</span>
              </div>
              <div className="flex items-center">
                <span className="w-3 h-3 inline-block bg-[#82ca9d] mr-1 rounded-full"></span>
                <span className="dark:text-gray-300">{t("studentProgress.charts.studentRecorded")}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trainingSessionsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#474747" />
                  <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <YAxis stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <Tooltip content={customTooltip} />
                  <Line
                    type="monotone"
                    dataKey="pesoMáximo"
                    stroke="#ff7300"
                    name={t("studentProgress.charts.maxWeight")}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="pesoTotal"
                    stroke="#82ca9d" 
                    name={t("studentProgress.charts.totalWeight")}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="dark:border-gray-700 dark:bg-gray-800">
          <CardHeader>
            <CardTitle className="dark:text-white">{t("studentProgress.charts.trainingVolume")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trainingSessionsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#474747" />
                  <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <YAxis stroke="#9CA3AF" tick={{ fill: "#E5E7EB" }} />
                  <Tooltip content={customTooltip} />
                  <Bar
                    dataKey="séries"
                    fill="#8884d8"
                    name={t("studentProgress.charts.totalSets")}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
